import { createFileRoute, Link } from "@tanstack/react-router";
import { CompanyInfo } from "@/components/CompanyInfo";

export const Route = createFileRoute("/eula")({
  head: () => ({
    meta: [
      { title: "End User Licence Agreement (EULA) — Recovery Trauma™" },
      {
        name: "description",
        content:
          "The licence agreement for the Recovery Trauma™ iOS app, including Apple In-App Purchase subscription terms.",
      },
      { property: "og:title", content: "End User Licence Agreement — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Licence, subscription and Apple-required terms for the Recovery Trauma™ app.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://recoverytrauma.com/eula" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/eula" }],
  }),
  component: EulaPage,
});

const SECTIONS: { title: string; body: (string | string[])[] }[] = [
  {
    title: "1. Who this agreement is between",
    body: [
      "This End User Licence Agreement (\"EULA\") is between you and Recovery Trauma™ (\"we\", \"us\"). It covers the Recovery Trauma™ mobile app (the \"App\"). It is concluded between you and us only, and not with Apple Inc. (\"Apple\"). We, not Apple, are solely responsible for the App and its content.",
      "By downloading or using the App you agree to this EULA and to our Terms of Service and Privacy Policy, which also apply. If this EULA conflicts with the Apple Media Services Terms and Conditions, those Apple terms take priority where they are more protective of you.",
      "You must be 18 or older to use the App.",
    ],
  },
  {
    title: "2. Your licence",
    body: [
      "We grant you a personal, non-transferable, non-exclusive, revocable licence to use the App on any Apple-branded device that you own or control, as permitted by the Usage Rules in the Apple Media Services Terms and Conditions. The App may also be used by other accounts linked to you through Family Sharing or volume purchasing where Apple allows it.",
      "You may not copy, modify, reverse-engineer, resell, rent or distribute the App or its content, remove our trademarks or notices, or use the App to break the law or harm others.",
    ],
  },
  {
    title: "3. Educational use only — not medical care",
    body: [
      "The App offers trauma education and self-care tools. It is not therapy, medical advice, a diagnosis or an emergency service. If you are in danger or crisis, contact your local emergency number or a crisis line straight away. Crisis resources are available throughout the App.",
    ],
  },
  {
    title: "4. Membership subscriptions through Apple",
    body: [
      "The App is free to download. Full access requires a Recovery Trauma™ membership. In the App you can buy an auto-renewing membership through Apple In-App Purchase:",
      [
        "Monthly — renews every month",
        "6-Month — renews every 6 months",
        "Yearly — renews every year",
      ],
      "Prices are shown in the App before you buy and may vary by country. Payment is charged to your Apple ID when you confirm the purchase. Your subscription renews automatically at the same price unless auto-renew is turned off at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours before the current period ends.",
      "You can manage or cancel your subscription at any time in your Apple ID account settings. Cancelling stops future renewals; you keep access until the end of the period you have paid for. Refunds for App purchases are handled by Apple under Apple's terms — we cannot cancel or refund an Apple subscription for you.",
      "Your membership is linked to the Recovery Trauma™ account you are signed in to when you buy. Use Restore Purchases to link it again on a new device. If you already have a membership bought on our website, simply sign in — you do not need to buy again.",
    ],
  },
  {
    title: "5. Maintenance and support",
    body: [
      "We are solely responsible for providing maintenance and support for the App, as described in this EULA and as required by law. Apple has no obligation to provide any maintenance or support services for the App. Contact us at info@recoverytrauma.com.",
    ],
  },
  {
    title: "6. Warranty",
    body: [
      "We are solely responsible for any product warranties, whether express or implied by law, to the extent they have not been effectively disclaimed. If the App fails to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any) of the App to you. To the maximum extent permitted by law, Apple has no other warranty obligation with respect to the App, and any other claims, losses, liabilities, damages, costs or expenses attributable to a failure to conform to a warranty are our sole responsibility.",
      "Nothing in this EULA limits your statutory rights as a consumer, including under the UK Consumer Rights Act 2015.",
    ],
  },
  {
    title: "7. Product claims",
    body: [
      "We, not Apple, are responsible for addressing any claims by you or any third party relating to the App or your possession and use of it, including (i) product liability claims; (ii) any claim that the App fails to meet any applicable legal or regulatory requirement; and (iii) claims arising under consumer protection, privacy or similar legislation.",
    ],
  },
  {
    title: "8. Intellectual property",
    body: [
      "If a third party claims that the App or your possession and use of it infringes that third party's intellectual property rights, we, not Apple, will be solely responsible for the investigation, defence, settlement and discharge of that claim.",
      "\"Recovery Trauma™\" and \"Healing Beyond Survival Mode™\" are our trademarks. The App's content remains ours or our licensors'. Your journals and private entries remain yours.",
    ],
  },
  {
    title: "9. Legal compliance",
    body: [
      "You confirm that (i) you are not located in a country that is subject to a U.S. Government embargo, or that has been designated by the U.S. Government as a \"terrorist supporting\" country; and (ii) you are not listed on any U.S. Government list of prohibited or restricted parties.",
    ],
  },
  {
    title: "10. Third-party terms",
    body: [
      "You must comply with any applicable third-party terms when using the App — for example, your mobile carrier or internet provider's terms.",
    ],
  },
  {
    title: "11. Ending this licence",
    body: [
      "This licence lasts until you or we end it. You can end it by deleting the App and, if you wish, your account (Profile → Privacy Settings). We may end it if you seriously breach this EULA or our Terms of Service. Deleting your account does not cancel an Apple subscription — cancel that in your Apple ID settings first.",
    ],
  },
  {
    title: "12. Limitation of liability",
    body: [
      "To the extent permitted by law, we are not liable for losses that were not foreseeable, or for business losses. We do not exclude liability for death or personal injury caused by our negligence, for fraud, or for anything else that cannot be excluded by law.",
    ],
  },
  {
    title: "13. Apple as third-party beneficiary",
    body: [
      "You and we acknowledge and agree that Apple and Apple's subsidiaries are third-party beneficiaries of this EULA, and that, upon your acceptance of this EULA, Apple will have the right (and will be deemed to have accepted the right) to enforce this EULA against you as a third-party beneficiary.",
    ],
  },
  {
    title: "14. Governing law and changes",
    body: [
      "This EULA is governed by the law of England and Wales. If you are a consumer living elsewhere, you keep the protection of the mandatory laws of your country. We may update this EULA; material changes will be notified in the App or by email.",
    ],
  },
];

function EulaPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-3xl text-foreground sm:text-4xl">End User Licence Agreement</h1>
      <p className="mt-2 text-sm text-muted-foreground">Recovery Trauma™ iOS app · Last updated: 28 September 2026</p>

      <div className="mt-8 space-y-8">
        {SECTIONS.map((s) => (
          <section key={s.title} aria-labelledby={s.title}>
            <h2 id={s.title} className="font-display text-xl text-foreground">
              {s.title}
            </h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/85 sm:text-base">
              {s.body.map((b, i) =>
                Array.isArray(b) ? (
                  <ul key={i} className="list-disc space-y-1 ps-6">
                    {b.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i}>{b}</p>
                ),
              )}
            </div>
          </section>
        ))}

        <section>
          <h2 className="font-display text-xl text-foreground">15. Contact us</h2>
          <p className="mt-3 text-sm text-foreground/85 sm:text-base">
            Questions, complaints or claims about the App:{" "}
            <a href="mailto:info@recoverytrauma.com" className="text-gold underline">
              info@recoverytrauma.com
            </a>
          </p>
          <div className="mt-3">
            <CompanyInfo />
          </div>
        </section>
      </div>

      <p className="mt-10 text-sm text-foreground/75">
        See also our <Link to="/terms" className="text-gold underline">Terms of Service</Link> and{" "}
        <Link to="/privacy" className="text-gold underline">Privacy Policy</Link>.
      </p>
    </div>
  );
}
